import { useMemo } from "react";
import type { DecimalString } from "@/lib/decimal";
import { balanceStore, type BalanceStore } from "./balanceStore";
import { useBalance } from "./useBalance";

const CAPTURE_TIMEOUT_MS = 15_000;

/** Resolves with the current stable reading, or the next one (the stream, or PRINT in keyboard mode). */
export function waitForStableReading(
  store: BalanceStore = balanceStore,
  timeoutMs = CAPTURE_TIMEOUT_MS,
): Promise<DecimalString> {
  const snapshot = store.getSnapshot();
  if (snapshot.status !== "connected") return Promise.reject(new Error("The balance isn't connected"));
  if (snapshot.stable && snapshot.reading?.grams) return Promise.resolve(snapshot.reading.grams);
  return new Promise((resolve, reject) => {
    const timer = window.setTimeout(() => {
      stop();
      reject(new Error("The balance didn't settle. Check for drafts and vibration, then try again."));
    }, timeoutMs);
    const stop = store.onReading((reading, stable) => {
      if (!stable || reading.grams === null) return;
      window.clearTimeout(timer);
      stop();
      resolve(reading.grams);
    });
  });
}

/**
 * The balance as a "read the weight" source for forms (matches `WeightCapture` in
 * `@/features/inventory/api`): null while no balance is connected, so forms show no balance button.
 */
export interface BalanceWeightSource {
  label: string;
  capture: () => Promise<DecimalString>;
  ready: boolean;
  status?: string;
}

export function useBalanceWeightSource(): BalanceWeightSource | null {
  const balance = useBalance();
  const { status, stable, identity } = balance;
  return useMemo(() => {
    if (status === "disconnected" || status === "error") return null;
    return {
      label: `Read ${identity.model ?? "balance"}`,
      capture: () => waitForStableReading(),
      ready: status === "connected",
      status: status === "connected" ? (stable ? "Stable" : "Settling") : "Reconnecting",
    };
  }, [status, stable, identity.model]);
}
